import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Paper,
  Button,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
} from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { TabContext, TabPanel } from "@mui/lab";
import { Link } from "react-router-dom";
import ActivityBar from "../../components/Reusable/ActivityBar/ActivityBar";
import { apiService } from "../../api/apiService";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import UploadResult from "./UploadResult";
import FullPageLoader from "../../components/Reusable/Loaders/FullPageLoader";


// Reusable select component
const DashboardSelect = <M extends boolean = true>({
  label,
  id,
  options,
  onChange,
  value,
  multiple = true as M,
  disabled = false,
}: {
  label: string;
  id: string;
  options: string[];
  onChange?: (value: M extends true ? string[] : string) => void;
  value?: string[] | string;
  multiple?: M;
  disabled?: boolean;
}) => {
  const processedValue = multiple
    ? (Array.isArray(value) ? value : [])
    : (typeof value === 'string' ? value : '');

  const allSelected = multiple && Array.isArray(processedValue) &&
    options.length > 0 && processedValue.length === options.length;

  const handleChange = (event: any) => {
    if (disabled) return;

    const { target: { value: newValue } } = event;

    if (multiple) {
      if (typeof newValue === "string" ? newValue.includes("All") : newValue.includes("All")) {
        const allOptions = allSelected ? [] : options;
        if (onChange) {
          onChange(allOptions as any);
        }
      } else {
        const processedValue = typeof newValue === "string" ? newValue.split(",") : newValue;
        if (onChange) {
          onChange(processedValue as any);
        }
      }
    } else {
      if (onChange) {
        onChange(newValue as any);
      }
    }
  };

  const isDisabled = disabled || (options.length === 0);

  return (
    <FormControl fullWidth>
      <Box sx={{ fontSize: "14px", marginBottom: "6px", fontWeight: 500 }}>{label}</Box>

      {multiple ? (
        <Select
          id={id}
          multiple
          displayEmpty
          disabled={isDisabled}
          value={Array.isArray(processedValue) ? processedValue : []}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={(selected) =>
            selected.length === 0 ?
              <span style={{ color: isDisabled ? "#ccc" : "#999" }}>Select</span> :
              selected.length === options.length ?
                <span style={{ color: isDisabled ? "#ccc" : "#000" }}>All Selected</span> :
                <span style={{ color: isDisabled ? "#ccc" : "#000" }}>{selected.join(", ")}</span>
          }
          sx={{
            '& .MuiSelect-select': {
              padding: '10px',
              color: isDisabled ? '#ccc' : '#000',
            },
            '& .MuiMenuItem-root': {
              color: '#000',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#ccc',
            }
          }}
        >
          <MenuItem key="all-option" value="All">
            <Checkbox
              checked={allSelected}
              indeterminate={Array.isArray(processedValue) && processedValue.length > 0 && processedValue.length < options.length}
            />
            <ListItemText primary="All" sx={{ color: '#000', fontWeight: 600 }} />
          </MenuItem>

          {options.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox checked={Array.isArray(processedValue) && processedValue.indexOf(option) > -1} />
              <ListItemText primary={option} sx={{ color: '#000' }} />
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Select
          id={id}
          displayEmpty
          disabled={isDisabled}
          value={typeof processedValue === 'string' ? processedValue : ''}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={(selected) =>
            selected === '' ?
              <span style={{ color: isDisabled ? "#ccc" : "#999" }}>Select</span> :
              <span style={{ color: isDisabled ? "#ccc" : "#000" }}>{selected}</span>
          }
          sx={{
            '& .MuiSelect-select': {
              padding: '10px',
              color: isDisabled ? '#ccc' : '#000',
            },
            '& .MuiMenuItem-root': {
              color: '#000',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#ccc',
            }
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option} sx={{ color: '#000' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  );
};


const ResultDashboard: React.FC = () => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = React.useState('');
  const [rows, setRows] = useState<ReturnType<typeof createData>[]>([]);


  const [filterValues, setFilterValues] = useState<{
    year: string;
    halfYear: string;
    type: string[];
    starBlock: string[];
    l2: string[];
    l3: string[];
    maturityLevel: string[];
  }>({
    year: "",
    halfYear: "",
    type: [],
    starBlock: [],
    l2: [],
    l3: [],
    maturityLevel: [],
  });

  const [filterOptions, setFilterOptions] = useState<{
    years: string[];
    halfYears: string[];
    types: string[];
    starBlocks: string[];
    l2s: string[];
    l3s: string[];
    maturityLevels: string[];
  }>({
    years: [],
    halfYears: ["H1", "H2"],
    types: [],
    starBlocks: [],
    l2s: [],
    l3s: [],
    maturityLevels: [],
  });

  const [dashboardMetrics, setDashboardMetrics] = useState<{
    total: number;
    penalty: number;
    fundamentalTarget: number;
    improvementScore: number;
    gap: number;
  }>({
    total: 0,
    penalty: 0,
    fundamentalTarget: 0,
    improvementScore: 0,
    gap: 0
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    if (newValue !== 'Upload') {
      handleApplyFilters(newValue);
    }
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      setIsLoading(true);
      const userRole = localStorage.getItem('role');
      const isAdmin = userRole === 'super_admin';
      const currentYear = new Date().getFullYear();
      const yearsArray: string[] = Array.from(
        { length: Math.max(currentYear - 2025 + 1, 1) },
        (_, i) => (Math.max(2025, currentYear) - i).toString()
      );

      const defaultYear = currentYear.toString();
      const defaultHalfYear = "H1";
      setFilterValues(prev => ({
        ...prev,
        year: defaultYear,
        halfYear: defaultHalfYear
      }));

      setFilterOptions(prev => ({ ...prev, years: yearsArray }));
      try {
        const jsonResponse = await apiService(
          "/static_management",
          "GET",
          undefined,
          undefined,
          instance as PublicClientApplication
        );

        if (jsonResponse?.status === "success" && Array.isArray(jsonResponse.data)) {
          const locationNames = jsonResponse.data.map((location: any) => location.name || location);
          const allSections = isAdmin ? ['Upload', ...locationNames] : [...locationNames];
          setSections(allSections);

          const defaultTab = isAdmin ? 'Upload' : (locationNames.length > 0 ? locationNames[0] : '');
          setSelectedTab(defaultTab);
          await fetchFilterOptions(defaultYear, defaultHalfYear);
          if (defaultTab !== 'Upload' && defaultTab !== '') {
            setTimeout(() => {
              handleApplyFilters(defaultTab);
            }, 100);
          }
        } else {
          console.warn("Unexpected format for locations data:", jsonResponse);
          if (isAdmin) {
            setSections(['Upload']);
            setSelectedTab('Upload');
          } else {
            setSections([]);
          }
        }
      } catch (err: any) {
        setError(err.message || "Error initializing dashboard");
        console.error("Dashboard initialization error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeDashboard();
  }, []);

  useEffect(() => {
    const defaultYear = new Date().getFullYear().toString();
    const defaultHalfYear = "H1";

    setFilterValues(prev => ({
      ...prev,
      year: defaultYear,
      halfYear: defaultHalfYear
    }));

    fetchFilterOptions(defaultYear, defaultHalfYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFilterOptions = async (year: string, halfYear: string) => {
    setIsLoading(true);
    try {
      const response = await apiService(
        `/ops/filter_dd_values/${year}/${halfYear}?section_name=SCORE%20SHEET`,
        "GET",
        undefined,
        undefined,
        instance as PublicClientApplication
      );

      if (response?.status === "success" && response.data) {
        mapApiDataToFilterOptions(response.data);
      } else {
        mapApiDataToFilterOptions(null);
      }
    } catch (err: any) {
      console.error("Error fetching filter options:", err);
      mapApiDataToFilterOptions(null);
    } finally {
      setIsLoading(false);
    }
  };

  const mapApiDataToFilterOptions = (apiData: any) => {
    if (!apiData || typeof apiData !== 'object') {
      setFilterOptions(prev => ({
        ...prev,
        types: [],
        starBlocks: [],
        l2s: [],
        l3s: [],
        maturityLevels: []
      }));
      return;
    }

    const mappedData: any = {};
    mappedData.types = Array.isArray(apiData.Type) && apiData.Type.length > 0
      ? apiData.Type
      : [];

    mappedData.starBlocks = Array.isArray(apiData['Star Block']) && apiData['Star Block'].length > 0
      ? apiData['Star Block']
      : [];

    mappedData.l2s = Array.isArray(apiData.L2) && apiData.L2.length > 0
      ? apiData.L2
      : [];

    mappedData.l3s = Array.isArray(apiData.L3) && apiData.L3.length > 0
      ? apiData.L3
      : [];

    mappedData.maturityLevels = Array.isArray(apiData['Maturity Level']) && apiData['Maturity Level'].length > 0
      ? apiData['Maturity Level']
      : [];

    setFilterOptions(prev => ({
      ...prev,
      ...mappedData
    }));

    setFilterValues(prev => ({
      ...prev,
      type: mappedData.types.length === 0 ? [] : prev.type,
      starBlock: mappedData.starBlocks.length === 0 ? [] : prev.starBlock,
      l2: mappedData.l2s.length === 0 ? [] : prev.l2,
      l3: mappedData.l3s.length === 0 ? [] : prev.l3,
      maturityLevel: mappedData.maturityLevels.length === 0 ? [] : prev.maturityLevel
    }));
  };

  const handleUploadSuccess = () => {
    fetchFilterOptions(filterValues.year, filterValues.halfYear);
  }

  const handleYearChange = (value: string) => {
    setFilterValues(prev => ({ ...prev, year: value }));
    fetchFilterOptions(value, filterValues.halfYear);
  };

  const handleHalfYearChange = (value: string) => {
    setFilterValues(prev => ({ ...prev, halfYear: value }));
    fetchFilterOptions(filterValues.year, value);
  };

  const handleTypeChange = (values: string[]) => {
    setFilterValues(prev => ({ ...prev, type: values }));
  };

  const handleStarBlockChange = (values: string[]) => {
    setFilterValues(prev => ({ ...prev, starBlock: values }));
  };

  const handleL2Change = (values: string[]) => {
    setFilterValues(prev => ({ ...prev, l2: values }));
  };

  const handleL3Change = (values: string[]) => {
    setFilterValues(prev => ({ ...prev, l3: values }));
  };

  const handleMaturityLevelChange = (values: string[]) => {
    setFilterValues(prev => ({ ...prev, maturityLevel: values }));
  };

  const handleApplyFilters = async (managementTab?: string) => {
    setIsLoading(true);

    try {
      const formattedFilters: any = {
        "TYPE": filterValues.type.length > 0 ? filterValues.type : ["ALL"],
        "STAR BLOCK": filterValues.starBlock.length > 0 ? filterValues.starBlock : ["ALL"],
        "L2 Description": filterValues.l2.length > 0 ? filterValues.l2 : ["ALL"],
        "L3 Description": filterValues.l3.length > 0 ? filterValues.l3 : ["ALL"],
        "Maturity Level": filterValues.maturityLevel.length > 0 ? filterValues.maturityLevel : ["ALL"]
      };

      const currentManagement = managementTab || selectedTab;

      const requestBody = {
        filters: formattedFilters,
        management: currentManagement,
        section_name: "SCORE SHEET"
      };

      const response = await apiService(
        `/ops/process_result_file/${filterValues.year}/${filterValues.halfYear}`,
        "POST",
        requestBody,
        undefined,
        instance as PublicClientApplication
      );

      if (response?.status === "success" && response.data) {
        processFilterResults(response.data, currentManagement);
        setError(null);
      } else {
        const errorMessage = response?.error?.message || "No data returned from filter API";
        console.warn(errorMessage);
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error("Error applying filters:", err);
      let errorMessage = "Error applying filters";

      if (err.response && err.response.data) {
        if (typeof err.response.data === 'object') {
          if (err.response.data.error && err.response.data.error.message) {
            errorMessage = err.response.data.error.message;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.detail) {
            errorMessage = err.response.data.detail;
          }
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      }
      else if (err.message && err.message.includes("Not Found")) {
        errorMessage = "No Result Found";
      }
      else if (err.message) {
        const messageMatch = err.message.match(/message:\s*([^,}]+)/i);
        if (messageMatch && messageMatch[1]) {
          errorMessage = messageMatch[1].trim();
          if (errorMessage === "Not Found") {
            errorMessage = "No Result Found";
          }
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const processFilterResults = (responseData: any, currentManagement?: string) => {
    if (responseData?.data && Array.isArray(responseData.data)) {
      const mappedRows = responseData.data.map((item: any) => {
        const managementToUse = currentManagement || selectedTab;

        return createData(
          item.TYPE || '',
          item['STAR BLOCK'] || '',
          item['L2 Description'] || '',
          item['L3 Description'] || '',
          item['Audit Requirements'] || '',
          item['Maturity Level'] || '',
          item[managementToUse] || 0
        );
      });
      setRows(mappedRows);
    } else {
      console.warn("Invalid or missing table data in API response");
      setRows([]);
    }

    const totalSum = responseData?.sum || 0;
    let metrics = { PENALTY: 0, "80% FUNDAMENTAL TARGET": 0, "20% IMPROVEMENT H2 SCORE": 0, GAP: 0 };

    if (responseData?.static_data && Array.isArray(responseData.static_data)) {
      const managementToUse = currentManagement || selectedTab;
      const managementData = responseData.static_data.find(
        (item: any) => item.Management === managementToUse
      );

      if (managementData) {
        metrics = {
          PENALTY: managementData.PENALTY || 0,
          "80% FUNDAMENTAL TARGET": managementData["80% FUNDAMENTAL TARGET"] || 0,
          "20% IMPROVEMENT H2 SCORE": managementData["20% IMPROVEMENT H2 SCORE"] || 0,
          GAP: managementData.GAP || 0
        };
      }
    }

    setDashboardMetrics({
      total: totalSum,
      penalty: metrics.PENALTY,
      fundamentalTarget: metrics["80% FUNDAMENTAL TARGET"],
      improvementScore: metrics["20% IMPROVEMENT H2 SCORE"],
      gap: metrics.GAP
    });
  };

  const breadcrumbs = [
    <Link key="1" color="#000" to="/">
      Home
    </Link>,
    <Link key="2" color="#000" to="/">
      Dashboard
    </Link>,
  ];

  function createData(
    name: string,
    calories: string,
    fat: string,
    carbs: string,
    protein: string,
    additionalProtein: string,
    score: number,
  ) {
    return { name, calories, fat, carbs, protein, additionalProtein, score };
  }


  return (
    <>
      {isLoading && (
        <FullPageLoader />
      )}

      <ActivityBar breadcrumbs={breadcrumbs} />

      <TabContext value={selectedTab}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            borderBottom: 1,
            borderColor: "divider",
            mx: 2,
            backgroundColor: "#fff",
            borderRadius: 1,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Dashboard tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: "Avantt",
                color: "#666",
                "&.Mui-selected": {
                  color: "#000",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#D7A833",
                height: 3,
              },
            }}
          >
            {sections.map((label) => (
              <Tab key={label} label={label} value={label} />
            ))}
          </Tabs>
        </Box>

        {sections.map((label) => (
          <TabPanel
            key={label}
            value={label}
            sx={{ padding: '10px !important' }}
          >
            {label === 'Upload' ? (
              <UploadResult onSuccess={handleUploadSuccess} />
            ) : (
              <>
                <Paper sx={{ pl: 2, pt: 3, pb: 3, pr: 2, mb: 2, mt: 1, }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", width: '100%' }}>
                    <Box sx={{ width: '90%', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <Box sx={{ width: '12%' }}>
                        <DashboardSelect
                          id="year"
                          label="Year"
                          options={filterOptions.years}
                          onChange={handleYearChange}
                          multiple={false}
                          value={filterValues.year}
                        />
                      </Box>
                      <Box sx={{ width: '12%' }}>
                        <DashboardSelect
                          id="half"
                          label="Half-yearly"
                          options={filterOptions.halfYears}
                          onChange={handleHalfYearChange}
                          multiple={false}
                          value={filterValues.halfYear}
                        />
                      </Box>
                      <Box sx={{ width: '12%' }}>
                        <DashboardSelect
                          id="type"
                          label="Type"
                          options={filterOptions.types}
                          onChange={handleTypeChange}
                          value={filterValues.type}
                          disabled={filterOptions.types.length === 0}
                        />
                      </Box>
                      <Box sx={{ width: '12%' }}>
                        <DashboardSelect
                          id="star"
                          label="Star Blocks"
                          options={filterOptions.starBlocks}
                          onChange={handleStarBlockChange}
                          value={filterValues.starBlock}
                          disabled={filterOptions.starBlocks.length === 0}
                        />
                      </Box>
                      <Box sx={{ width: '12%' }}>
                        <DashboardSelect
                          id="l2"
                          label="L2"
                          options={filterOptions.l2s}
                          onChange={handleL2Change}
                          value={filterValues.l2}
                          disabled={filterOptions.l2s.length === 0}
                        />
                      </Box>
                      <Box sx={{ width: '12%' }}>
                        <DashboardSelect
                          id="l3"
                          label="L3"
                          options={filterOptions.l3s}
                          onChange={handleL3Change}
                          value={filterValues.l3}
                          disabled={filterOptions.l3s.length === 0}
                        />
                      </Box>
                      <Box sx={{ width: '15%' }}>
                        <DashboardSelect
                          id="maturity"
                          label="Maturity Level"
                          options={filterOptions.maturityLevels}
                          onChange={handleMaturityLevelChange}
                          value={filterValues.maturityLevel}
                          disabled={filterOptions.maturityLevels.length === 0}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexFlow: 'column', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={(e) => handleApplyFilters()}
                        sx={{
                          backgroundColor: "linear-gradient(238deg, #D7A833 1.68%, #F4DF03 91.8%)",
                          color: "#000",
                          textTransform: "none",
                          fontFamily: "Avantt",
                          "&:hover": {
                            backgroundColor: "linear-gradient(238deg, #D7A833 1.68%, #F4DF03 91.8%)",
                          },
                          mt: 2
                        }}>
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Paper>

                {!isLoading && error && (
                  <Paper sx={{ marginTop: '20px', padding: '10px', textAlign: 'center', }}>
                    <Typography variant="h6" sx={{ color: '#222', fontWeight: 500 }}>
                      {error}
                    </Typography>
                  </Paper>
                )}

                {!isLoading && !error && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        px: 1,

                      }}
                    >
                      <Box sx={{ flex: "1", minWidth: "18%", padding: '10px', backgroundColor: "#fff", borderRadius: 1, }}>
                        <Box
                          sx={{ display: "flex", height: "100%" }}
                        >
                          {/* Icon */}
                          <Box sx={{
                            backgroundColor: "#EBF2F6",
                            minHeight: "120px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "40%",
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                              <g clipPath="url(#clip0_10689_3073)">
                                <path d="M12.9195 0C6.29234 0 0.919525 5.37281 0.919525 12C0.919525 18.6272 6.29234 24 12.9195 24C19.5467 24 24.9195 18.6272 24.9195 12C24.9195 5.37281 19.5467 0 12.9195 0ZM17.1331 18.1392H8.36187V17.2111L11.5948 12.12L8.56859 6.6525V5.86125H16.8922L16.9953 9.33516H15.7573L15.5511 8.13141C15.5052 7.87922 15.4273 7.67297 15.3181 7.51172C15.2094 7.35187 15.0523 7.23047 14.8451 7.15125C14.6389 7.07062 14.3637 7.03031 14.0202 7.03031H12.2834L14.3816 11.2261V11.4839L11.8878 15.6628H14.4336C14.9722 15.6628 15.3589 15.5287 15.5937 15.2592C15.8286 14.9897 15.9866 14.6208 16.0667 14.1492L16.1703 13.5136H17.4772L17.1336 18.1392H17.1331Z" fill="#254F64" />
                              </g>
                              <defs>
                                <clipPath id="clip0_10689_3073">
                                  <rect width="24" height="24" fill="white" transform="translate(0.919525)" />
                                </clipPath>
                              </defs>
                            </svg>
                          </Box>

                          <Box sx={{ display: "flex", gap: "10px", flexDirection: "column", justifyContent: "space-around", width: "60%", padding: "10px", backgroundColor: "#fff" }}>
                            <Box sx={{ fontSize: "14px", fontWeight: 500, color: "#71717A" }}>Total</Box>
                            <Box sx={{ fontSize: "24px", fontWeight: 700, color: "#0D0D0D" }}>{dashboardMetrics.total}</Box>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ flex: "1", minWidth: "18%", padding: '10px', backgroundColor: "#fff", borderRadius: 1, }}>
                        <Box
                          sx={{ display: "flex", height: "100%" }}
                        >
                          {/* Icon */}
                          <Box sx={{
                            backgroundColor: "#F8EA9D",
                            minHeight: "120px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "40%",
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="31" height="30" viewBox="0 0 31 30" fill="none">
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M13.703 23.58H13.5839C13.4269 23.58 13.2976 23.4567 13.2902 23.2998L13.2451 22.3447L12.0325 23.499C11.9778 23.551 11.9052 23.58 11.8297 23.58H11.7273C11.604 23.58 11.4938 23.5031 11.4513 23.3874L10.5749 21.005L9.19877 23.4961C9.17018 23.5479 9.11571 23.58 9.05654 23.58H9.05407C9.01501 23.58 8.98289 23.5493 8.98118 23.5103L8.89777 21.6128L6.90277 23.4996C6.84812 23.5513 6.77583 23.58 6.70065 23.58H6.61136C6.49624 23.58 6.39171 23.5129 6.34383 23.4081L5.85789 22.3447L5.37195 23.4081C5.32412 23.5129 5.2196 23.58 5.10448 23.58H4.46054C4.34542 23.58 4.24089 23.5129 4.19301 23.4081L3.35895 21.5828L2.97865 22.4151C2.93083 22.5199 2.82624 22.587 2.71112 22.587H1.8073C1.64489 22.587 1.51318 22.4553 1.51318 22.2929V4.98432H5.20477C5.43218 4.98432 5.61654 4.79997 5.61654 4.57256V0.882324H19.1448C19.3072 0.882324 19.4389 1.01403 19.4389 1.17644V15.6824L17.6661 15.2072C16.7836 14.9699 15.8689 15.4975 15.6314 16.3806L15.6312 16.3814L15.3895 17.2837H3.95012C3.72283 17.2837 3.53836 17.4683 3.53836 17.6955C3.53836 17.9227 3.72283 18.1073 3.95012 18.1073H15.1689L13.703 23.58ZM10.0572 3.96991C9.38365 4.14038 8.90013 4.71115 8.90013 5.37015C8.90013 6.1575 9.59365 6.82209 10.4767 6.82209C10.8798 6.82209 11.2285 7.0895 11.2285 7.4505C11.2285 7.81068 10.8798 8.07891 10.4767 8.07891C10.1607 8.07891 9.99842 7.94556 9.78518 7.74385C9.62007 7.58768 9.35924 7.59491 9.20307 7.76003C9.04689 7.92509 9.05418 8.18591 9.21924 8.34209C9.49954 8.60721 9.74065 8.78491 10.0728 8.8608L10.0785 9.10221C10.0838 9.32938 10.2726 9.5095 10.4998 9.50421C10.7269 9.49885 10.9071 9.31009 10.9018 9.08291L10.8964 8.85038C11.5687 8.67932 12.052 8.10779 12.052 7.4505C12.052 6.66115 11.3581 5.99856 10.4767 5.99856C10.0726 5.99856 9.72365 5.73044 9.72365 5.37015C9.72365 5.00897 10.0726 4.74174 10.4767 4.74174C10.6096 4.74174 10.804 4.80544 10.9669 4.8915C11.0491 4.93491 11.1272 4.97885 11.1649 5.03621C11.2898 5.22603 11.5454 5.27874 11.7352 5.15379C11.9251 5.02885 11.9778 4.77327 11.8528 4.58344C11.7522 4.4305 11.5707 4.27903 11.3515 4.16332C11.2031 4.08491 11.0384 4.02232 10.8813 3.98003L10.8736 3.67868C10.8678 3.4515 10.6787 3.2718 10.4515 3.27756C10.2243 3.28338 10.0445 3.47256 10.0504 3.69974L10.0572 3.96991ZM3.95012 11.9506H17.002C17.2292 11.9506 17.4138 11.7661 17.4138 11.5389C17.4138 11.3116 17.2292 11.1271 17.002 11.1271H3.95012C3.72283 11.1271 3.53836 11.3116 3.53836 11.5389C3.53836 11.7661 3.72283 11.9506 3.95012 11.9506ZM3.95012 15.0283H17.002C17.2292 15.0283 17.4138 14.8437 17.4138 14.6165C17.4138 14.3893 17.2292 14.2047 17.002 14.2047H3.95012C3.72283 14.2047 3.53836 14.3893 3.53836 14.6165C3.53836 14.8437 3.72283 15.0283 3.95012 15.0283ZM1.92512 4.16079L4.79301 1.29391V4.16079H1.92512ZM21.0538 20.7357L28.9499 22.8516C29.5228 23.005 29.8652 23.5987 29.7118 24.1717C29.5585 24.7437 28.9647 25.087 28.3917 24.9337L20.4958 22.8178L21.0538 20.7357ZM14.8149 22.6116L16.0307 18.0731L20.5873 19.295L19.3713 23.8323L14.8149 22.6116ZM19.1581 24.6278L18.9749 25.3117C18.8561 25.757 18.3949 26.0232 17.9497 25.9034L15.0106 25.1157C14.5653 24.997 14.2991 24.5359 14.4188 24.0906L14.6019 23.4071L19.1581 24.6278ZM20.8004 18.4995L16.2437 17.2776L16.4267 16.5944C16.5464 16.1491 17.0076 15.883 17.4529 16.0027L20.3919 16.7904C20.8372 16.9091 21.1035 17.3703 20.9837 17.8156L20.8004 18.4995ZM20.67 27.3829C21.2945 27.3829 21.8061 27.8935 21.8061 28.5179V29.1176H12.9918V28.5179C12.9918 27.8935 13.5035 27.3829 14.1279 27.3829H20.67Z" fill="#8E7A06" />
                            </svg>
                          </Box>

                          <Box sx={{ display: "flex", gap: "10px", flexDirection: "column", justifyContent: "space-around", width: "60%", padding: "10px", backgroundColor: "#fff" }}>
                            <Box sx={{ fontSize: "14px", fontWeight: 500, color: "#71717A" }}>Penalty</Box>
                            <Box sx={{ fontSize: "24px", fontWeight: 700, color: "#0D0D0D" }}>{dashboardMetrics.penalty}</Box>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ flex: "1", minWidth: "18%", padding: '10px', backgroundColor: "#fff", borderRadius: 1, }}>
                        <Box
                          sx={{ display: "flex", height: "100%" }}
                        >
                          {/* Icon */}
                          <Box sx={{
                            backgroundColor: "#F4E8E9",
                            minHeight: "120px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "40%",
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="34" viewBox="0 0 36 34" fill="none">
                              <g clip-path="url(#clip0_10689_3139)">
                                <path d="M18.3421 30.0842C25.8373 30.0842 31.9133 24.1817 31.9133 16.9007C31.9133 9.61971 25.8373 3.71729 18.3421 3.71729C10.847 3.71729 4.77094 9.61971 4.77094 16.9007C4.77094 24.1817 10.847 30.0842 18.3421 30.0842Z" fill="#F4E8E9" />
                                <path d="M18.3421 24.7934C22.8294 24.7934 26.467 21.2597 26.467 16.9006C26.467 12.5415 22.8294 9.00781 18.3421 9.00781C13.8548 9.00781 10.2172 12.5415 10.2172 16.9006C10.2172 21.2597 13.8548 24.7934 18.3421 24.7934Z" fill="#851818" />
                                <path d="M20.0214 14.5513C20.0214 15.4537 19.2696 16.1841 18.3406 16.1841C17.4142 16.1841 16.6625 15.4537 16.6625 14.5513C16.6625 13.6514 17.4143 12.9185 18.3406 12.9185C19.2696 12.9185 20.0214 13.6514 20.0214 14.5513Z" fill="white" />
                                <path d="M20.5056 19.6566V19.6669C20.503 20.1928 20.0426 20.6091 19.5012 20.6091H17.1845C16.6242 20.6091 16.169 20.1641 16.1787 19.6199C16.1807 19.5093 16.1917 19.3968 16.2123 19.2829C16.3786 18.3653 17.174 17.6512 18.1288 17.563C19.4203 17.4437 20.5056 18.43 20.5056 19.6566Z" fill="white" />
                                <path d="M18.3421 8.66602C13.6676 8.66602 9.86481 12.3602 9.86481 16.9009C9.86481 21.442 13.6676 25.1362 18.3421 25.1362C23.0164 25.1362 26.8192 21.442 26.8192 16.9009C26.8192 12.3602 23.0164 8.66602 18.3421 8.66602ZM18.3421 24.4517C14.0563 24.4517 10.5695 21.0644 10.5695 16.901C10.5695 12.7376 14.0563 9.3506 18.3421 9.3506C22.6278 9.3506 26.1146 12.7376 26.1146 16.901C26.1146 21.0644 22.6278 24.4517 18.3421 24.4517Z" fill="#851818" />
                                <path d="M34.9006 16.6577H32.2592C32.1268 9.42595 26.1257 3.56447 18.6943 3.38371V0.914523C18.6943 0.725332 18.5366 0.572266 18.342 0.572266C18.1475 0.572266 17.9897 0.725332 17.9897 0.914523V3.38377C10.5584 3.56453 4.55722 9.42601 4.42488 16.6578H1.78354C1.58899 16.6578 1.43121 16.8109 1.43121 17C1.43121 17.1892 1.58899 17.3423 1.78354 17.3423H4.43014C4.66755 24.4826 10.6266 30.2387 17.9898 30.4178V33.0855C17.9898 33.2747 18.1475 33.4278 18.3421 33.4278C18.5366 33.4278 18.6944 33.2747 18.6944 33.0855V30.4178C26.0576 30.2387 32.0166 24.4826 32.254 17.3423H34.9006C35.0952 17.3423 35.253 17.1892 35.253 17C35.253 16.8108 35.0952 16.6577 34.9006 16.6577ZM18.6944 29.7332V27.0831C18.6944 26.8939 18.5366 26.7408 18.3421 26.7408C18.1475 26.7408 17.9898 26.8939 17.9898 27.0831V29.7332C11.015 29.5545 5.37165 24.1048 5.13479 17.3422H7.96254C8.15709 17.3422 8.31487 17.1891 8.31487 16.9999C8.31487 16.8107 8.15709 16.6577 7.96254 16.6577H5.12959C5.26173 9.8036 10.9469 4.24865 17.9898 4.06822V6.91685C17.9898 7.10604 18.1475 7.25911 18.3421 7.25911C18.5366 7.25911 18.6944 7.10604 18.6944 6.91685V4.06822C25.7373 4.24871 31.4225 9.8036 31.5546 16.6577H28.7216C28.5271 16.6577 28.3693 16.8108 28.3693 17C28.3693 17.1892 28.5271 17.3422 28.7216 17.3422H31.5494C31.3125 24.1048 25.6692 29.5545 18.6944 29.7332Z" fill="#851818" />
                                <path d="M18.3407 16.526C19.4617 16.526 20.3737 15.64 20.3737 14.5511C20.3737 13.4622 19.4617 12.5762 18.3407 12.5762C17.2212 12.5762 16.3103 13.4622 16.3103 14.5511C16.3103 15.64 17.2211 16.526 18.3407 16.526ZM18.3407 13.2607C19.0732 13.2607 19.6691 13.8396 19.6691 14.5511C19.6691 15.2626 19.0732 15.8415 18.3407 15.8415C17.6096 15.8415 17.0149 15.2626 17.0149 14.5511C17.0149 13.8396 17.6096 13.2607 18.3407 13.2607Z" fill="#851818" />
                                <path d="M18.0954 17.2221C16.8576 17.3364 15.85 18.3374 15.8263 19.6134C15.8135 20.3468 16.4234 20.951 17.1845 20.951H19.5012C20.2455 20.951 20.8541 20.3755 20.858 19.6666V19.6562C20.858 18.2534 19.619 17.0803 18.0954 17.2221ZM20.1533 19.6649C20.1516 19.9965 19.859 20.2665 19.5012 20.2665H17.1845C17.0071 20.2665 16.841 20.199 16.7168 20.076C16.1946 19.5602 16.8112 18.0282 18.1622 17.9032C19.2765 17.8042 20.1533 18.6563 20.1533 19.6561C20.1533 19.6905 20.1533 19.6992 20.1533 19.6649Z" fill="white" />
                              </g>
                              <defs>
                                <clipPath id="clip0_10689_3139">
                                  <rect width="35" height="34" fill="white" transform="translate(0.842102)" />
                                </clipPath>
                              </defs>
                            </svg>
                          </Box>

                          <Box sx={{ display: "flex", gap: "10px", flexDirection: "column", justifyContent: "space-around", width: "60%", padding: "10px", backgroundColor: "#fff" }}>
                            <Box sx={{ fontSize: "14px", fontWeight: 500, color: "#71717A" }}>80% Fundamental Target</Box>
                            <Box sx={{ fontSize: "24px", fontWeight: 700, color: "#0D0D0D" }}>{dashboardMetrics.fundamentalTarget}</Box>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ flex: "1", minWidth: "18%", padding: '10px', backgroundColor: "#fff", borderRadius: 1, }}>
                        <Box
                          sx={{ display: "flex", height: "100%" }}
                        >
                          {/* Icon */}
                          <Box sx={{
                            backgroundColor: "#FBF5F1",
                            minHeight: "120px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "40%",
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                              <path d="M15.5203 25.271C14.9959 25.271 14.493 25.0626 14.1222 24.6918C13.7513 24.321 13.543 23.8181 13.543 23.2937C13.543 22.7693 13.7513 22.2663 14.1222 21.8955C14.493 21.5247 14.9959 21.3164 15.5203 21.3164C16.6123 21.3164 20.793 23.2937 20.793 23.2937C20.793 23.2937 16.6123 25.271 15.5203 25.271Z" fill="#805F47" />
                              <path d="M7.88487 12.7342C7.79122 12.7338 7.70028 12.7028 7.62585 12.646L6.3715 11.7058C6.34888 11.6888 6.32134 11.6796 6.29303 11.6796C6.26471 11.6796 6.23717 11.6888 6.21456 11.7058L4.96023 12.6463C4.88578 12.703 4.79483 12.7339 4.70122 12.7342C4.63302 12.7338 4.56584 12.7176 4.50501 12.6868C4.44418 12.656 4.39136 12.6114 4.35076 12.5566C4.31054 12.5027 4.28364 12.44 4.27226 12.3737C4.26088 12.3074 4.26533 12.2394 4.28527 12.1752L4.77556 10.5423C4.78313 10.5173 4.78302 10.4906 4.77524 10.4657C4.76745 10.4408 4.75235 10.4187 4.7319 10.4025L3.6628 9.54699C3.59274 9.49094 3.54182 9.41451 3.51706 9.32827C3.4923 9.24202 3.49494 9.15022 3.5246 9.06554C3.55426 8.98085 3.60949 8.90747 3.68265 8.85553C3.75582 8.80358 3.8433 8.77564 3.93303 8.77556H5.15785C5.18348 8.77556 5.20854 8.76805 5.22994 8.75394C5.25133 8.73984 5.26812 8.71976 5.27821 8.69621L5.89477 7.25718C5.92819 7.17925 5.98373 7.11284 6.05452 7.06617C6.12531 7.0195 6.20824 6.99463 6.29303 6.99463C6.37782 6.99463 6.46074 7.0195 6.53153 7.06617C6.60232 7.11284 6.65786 7.17925 6.69128 7.25718L7.30784 8.69591C7.31787 8.71954 7.33463 8.73969 7.35603 8.75385C7.37744 8.76801 7.40254 8.77556 7.4282 8.77556H8.65305C8.74278 8.77564 8.83027 8.80358 8.90343 8.85553C8.97659 8.90747 9.03182 8.98085 9.06148 9.06554C9.09114 9.15022 9.09378 9.24202 9.06902 9.32827C9.04427 9.41451 8.99334 9.49094 8.92328 9.54699L7.8548 10.4022C7.83417 10.4184 7.81891 10.4404 7.81102 10.4654C7.80312 10.4904 7.80296 10.5172 7.81055 10.5423L8.30084 12.1752C8.32077 12.2394 8.32523 12.3074 8.31385 12.3737C8.30246 12.44 8.27557 12.5027 8.23535 12.5566C8.19474 12.6114 8.14193 12.656 8.0811 12.6868C8.02026 12.7176 7.95306 12.7339 7.88487 12.7342Z" fill="#805F47" />
                              <path d="M25.4057 12.7342C25.3121 12.7338 25.2212 12.7028 25.1467 12.646L23.8924 11.7058C23.8698 11.6888 23.8422 11.6796 23.8139 11.6796C23.7856 11.6796 23.758 11.6888 23.7354 11.7058L22.4811 12.6463C22.4067 12.703 22.3157 12.7339 22.2221 12.7342C22.1539 12.7338 22.0867 12.7176 22.0259 12.6868C21.9651 12.656 21.9122 12.6114 21.8716 12.5566C21.8314 12.5027 21.8045 12.44 21.7931 12.3737C21.7818 12.3074 21.7862 12.2394 21.8061 12.1752L22.2964 10.5423C22.304 10.5173 22.3039 10.4906 22.2961 10.4657C22.2883 10.4408 22.2732 10.4187 22.2528 10.4025L21.1837 9.54699C21.1136 9.49094 21.0627 9.41451 21.0379 9.32827C21.0132 9.24202 21.0158 9.15022 21.0455 9.06554C21.0751 8.98085 21.1304 8.90747 21.2035 8.85553C21.2767 8.80358 21.3642 8.77564 21.4539 8.77556H22.6788C22.7044 8.77556 22.7294 8.76805 22.7508 8.75394C22.7722 8.73984 22.789 8.71976 22.7991 8.69621L23.4157 7.25718C23.4491 7.17925 23.5046 7.11284 23.5754 7.06617C23.6462 7.0195 23.7291 6.99463 23.8139 6.99463C23.8987 6.99463 23.9816 7.0195 24.0524 7.06617C24.1232 7.11284 24.1788 7.17925 24.2122 7.25718L24.8287 8.69591C24.8388 8.71954 24.8555 8.73969 24.8769 8.75385C24.8983 8.76801 24.9234 8.77556 24.9491 8.77556H26.174C26.2637 8.77564 26.3512 8.80358 26.4243 8.85553C26.4975 8.90747 26.5527 8.98085 26.5824 9.06554C26.612 9.15022 26.6147 9.24202 26.5899 9.32827C26.5652 9.41451 26.5142 9.49094 26.4442 9.54699L25.3757 10.4022C25.355 10.4184 25.3398 10.4404 25.3319 10.4654C25.324 10.4904 25.3238 10.5172 25.3314 10.5423L25.8217 12.1752C25.8416 12.2394 25.8461 12.3074 25.8347 12.3737C25.8233 12.44 25.7964 12.5027 25.7562 12.5566C25.7156 12.6114 25.6628 12.656 25.602 12.6868C25.5411 12.7176 25.4739 12.7339 25.4057 12.7342Z" fill="#805F47" />
                              <path d="M16.8217 10.9219C16.7215 10.9215 16.6242 10.8885 16.5444 10.8278L15.1508 9.78256C15.1228 9.76142 15.0886 9.75003 15.0535 9.75013C15.0182 9.75012 14.9838 9.7615 14.9555 9.78258L13.5625 10.8278C13.4828 10.8885 13.3854 10.9215 13.2852 10.9219C13.212 10.9215 13.1398 10.9041 13.0745 10.8709C13.0091 10.8378 12.9524 10.7899 12.9088 10.731C12.8657 10.6733 12.8368 10.6062 12.8246 10.5352C12.8124 10.4641 12.8172 10.3913 12.8386 10.3224L13.3832 8.50787C13.3924 8.4768 13.3922 8.44367 13.3826 8.41271C13.3729 8.38176 13.3542 8.3544 13.3289 8.33412L12.1412 7.38421C12.066 7.32406 12.0114 7.24204 11.9849 7.14949C11.9583 7.05694 11.9612 6.95843 11.9931 6.86757C12.0249 6.77672 12.0842 6.69801 12.1628 6.64232C12.2413 6.58664 12.3352 6.55673 12.4315 6.55672H13.7921C13.8237 6.55689 13.8548 6.54768 13.8812 6.53025C13.9077 6.51282 13.9284 6.48796 13.9407 6.45878L14.6269 4.85955C14.6627 4.77604 14.7221 4.70485 14.798 4.65482C14.8738 4.60479 14.9626 4.57812 15.0535 4.57812C15.1443 4.57813 15.2332 4.60479 15.309 4.65482C15.3848 4.70485 15.4443 4.77604 15.4801 4.85955L16.1657 6.45818C16.1781 6.48747 16.1989 6.51245 16.2254 6.52998C16.252 6.5475 16.2831 6.5568 16.3149 6.55671H17.6755C17.7718 6.55671 17.8657 6.58662 17.9442 6.64231C18.0228 6.69799 18.0821 6.7767 18.1139 6.86755C18.1458 6.95841 18.1487 7.05692 18.1221 7.14947C18.0956 7.24202 18.0409 7.32404 17.9658 7.38419L16.7781 8.3344C16.7528 8.3546 16.7341 8.38191 16.7244 8.41282C16.7147 8.44373 16.7145 8.47683 16.7238 8.50786L17.2684 10.3224C17.2898 10.3912 17.2946 10.4641 17.2824 10.5351C17.2701 10.6062 17.2413 10.6733 17.1982 10.731C17.1546 10.7899 17.0978 10.8378 17.0325 10.8709C16.9672 10.9041 16.895 10.9215 16.8217 10.9219Z" fill="#805F47" />
                              <path d="M6.31964 15.5561L6.43705 15.4496C8.63575 13.4651 11.4522 12.3006 14.4104 12.153L14.5691 12.145V17.5238L14.4304 17.535C12.8839 17.6616 11.4162 18.2705 10.2343 19.2758L10.1281 19.3655L6.31964 15.5561Z" fill="#805F47" />
                              <path d="M19.8726 19.2761C18.6909 18.2705 17.2231 17.6615 15.6765 17.535L15.5378 17.5238V12.145L15.6966 12.153C18.6547 12.3009 21.4712 13.4654 23.6699 15.4499L23.7873 15.5567L19.9782 19.3658L19.8726 19.2761Z" fill="#805F47" />
                              <path d="M3.00437 25.1268C2.89645 25.1266 2.78968 25.1045 2.69051 25.0619C2.59134 25.0194 2.50182 24.9571 2.42734 24.879C2.35535 24.8043 2.29946 24.7156 2.26314 24.6185C2.22681 24.5213 2.21082 24.4177 2.21615 24.3141C2.37214 21.36 3.54127 18.5499 5.52668 16.357L5.63347 16.2393L9.44373 20.0489L9.35346 20.1551C8.33198 21.3531 7.7188 22.8455 7.6029 24.4156C7.58607 24.6106 7.49638 24.792 7.35172 24.9239C7.20705 25.0557 7.01803 25.1281 6.82232 25.1268H3.00437Z" fill="#805F47" />
                              <path d="M23.2846 25.1268C23.0889 25.1281 22.8998 25.0556 22.7552 24.9237C22.6105 24.7919 22.5208 24.6103 22.5041 24.4153C22.3879 22.8455 21.775 21.3535 20.7541 20.1554L20.6638 20.0492L24.4735 16.2393L24.5803 16.357C26.5657 18.5499 27.735 21.36 27.8914 24.3141C27.8966 24.4178 27.8804 24.5215 27.8438 24.6187C27.8073 24.7159 27.7512 24.8046 27.679 24.8793C27.6046 24.9573 27.5152 25.0194 27.4161 25.062C27.317 25.1045 27.2104 25.1266 27.1026 25.1268H23.2846Z" fill="#805F47" />
                            </svg>
                          </Box>

                          <Box sx={{ display: "flex", gap: "10px", flexDirection: "column", justifyContent: "space-around", width: "60%", padding: "10px", backgroundColor: "#fff" }}>
                            <Box sx={{ fontSize: "14px", fontWeight: 500, color: "#71717A" }}>20% Improvement H2 Score</Box>
                            <Box sx={{ fontSize: "24px", fontWeight: 700, color: "#0D0D0D" }}>{dashboardMetrics.improvementScore}</Box>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ flex: "1", minWidth: "18%", padding: '10px', backgroundColor: "#fff", borderRadius: 1, }}>
                        <Box
                          sx={{ display: "flex", height: "100%" }}
                        >
                          {/* Icon */}
                          <Box sx={{
                            backgroundColor: "#FFD4D5",
                            minHeight: "120px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "40%",
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                              <g clip-path="url(#clip0_10689_3198)">
                                <path d="M12.7829 24.1905C19.4109 24.1905 24.7839 18.8174 24.7839 12.1895C24.7839 5.56152 19.4109 0.188477 12.7829 0.188477C6.15499 0.188477 0.781982 5.56152 0.781982 12.1895C0.781982 18.8174 6.15499 24.1905 12.7829 24.1905Z" fill="#B01212" />
                                <path d="M11.0697 8.75977V15.6195H12.569V14.9765H12.996V15.6195H14.497V8.75977H12.996V9.40098H12.569V8.75977H11.0697ZM12.569 9.8313H12.996V10.6893H12.569V9.8313ZM12.569 11.1163H12.996V11.9736H12.569V11.1163ZM12.569 12.4039H12.996V13.2611H12.569V12.4039ZM12.569 13.6889H12.996V14.5461H12.569V13.6889Z" fill="#FFD4D5" />
                                <path d="M7.20959 8.75977H10.6394V15.6195H7.20959V8.75977Z" fill="#FFD4D5" />
                                <path d="M14.9274 8.75977H18.3572V15.6195H14.9274V8.75977Z" fill="#FFD4D5" />
                              </g>
                              <defs>
                                <clipPath id="clip0_10689_3198">
                                  <rect width="24" height="24" fill="white" transform="translate(0.764771)" />
                                </clipPath>
                              </defs>
                            </svg>
                          </Box>

                          <Box sx={{ display: "flex", gap: "10px", flexDirection: "column", justifyContent: "space-around", width: "60%", padding: "10px", backgroundColor: "#fff" }}>
                            <Box sx={{ fontSize: "14px", fontWeight: 500, color: "#71717A" }}>GAP</Box>
                            <Box sx={{ fontSize: "24px", fontWeight: 700, color: "#0D0D0D" }}>{dashboardMetrics.gap}</Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <TableContainer>
                      <Table sx={{ minWidth: 650, borderCollapse: 'collapse', marginTop: '20px', }} aria-label="customized table">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#000' }}>
                            {['Type', 'Star Block', 'L2 Description', 'L3 Description', 'Audit Requirements', 'Maturity Level', 'Score'].map((col, index) => (
                              <TableCell
                                key={index}
                                align={index === 0 ? 'left' : 'center'}
                                sx={{
                                  color: '#fff',
                                  border: 'none',
                                  borderRight: 'none',
                                  borderLeft: 'none'
                                }}
                              >
                                {col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow
                              key={row.name}
                              sx={{
                                '& td, & th': {
                                  color: '#000',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  border: 'none',
                                  borderRight: 'none',
                                  borderLeft: 'none',
                                  borderBottom: '1px solid #000',
                                },
                              }}
                            >
                              <TableCell component="th" scope="row">{row.name}</TableCell>
                              <TableCell align="center">{row.calories}</TableCell>
                              <TableCell align="center">{row.fat}</TableCell>
                              <TableCell align="center">{row.carbs}</TableCell>
                              <TableCell align="center">{row.protein}</TableCell>
                              <TableCell align="center">{row.additionalProtein}</TableCell>
                              <TableCell sx={{ backgroundColor: row.score === 0 ? '#FFD6D6' : '#C7F2D3' }} align="center">{row.score}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </TabPanel>
        ))}
      </TabContext>
    </>
  );
};

export default ResultDashboard;
